import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import { getApiErrorMessage } from "../utils/error.utils";
import {
  getPricingPlans,
  getPricingPlan,
  createPricingPlan,
  updatePricingPlan,
  updatePricingPlanPosition,
  updatePricingPlanFeatures,
  deletePricingPlan,
  getFeatures,
  getFeature,
  createFeature,
  updateFeature,
  updateFeatureOrder,
  deleteFeature,
  buyPricingPlan,
  assignPricingPlan,
  cancelPricingPlan,
  resumePricingPlan,
  retryFailedPayment,
  getPricingPlanHistory,
  getPaymentInfo,
} from "./pricing-plan.api";
import type {
  CreatePricingPlanRequest,
  UpdatePricingPlanRequest,
  UpdatePricingPlanPositionRequest,
  UpdatePricingPlanFeaturesRequest,
  CreateFeatureRequest,
  UpdateFeatureRequest,
  UpdateFeatureOrderRequest,
  BuyPricingPlanRequest,
  AssignPricingPlanRequest,
  CancelPricingPlanRequest,
  ResumePricingPlanRequest,
  GetPricingPlanHistoryQuery,
} from "./pricing-plan.type";

const apiErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { error?: string; message?: string } } })
    ?.response?.data;
  return data?.error || data?.message || (error as { message?: string })?.message || fallback;
};

// Query Keys
export const pricingPlanKeys = {
  all: ["pricing-plans"] as const,
  lists: () => [...pricingPlanKeys.all, "list"] as const,
  list: () => [...pricingPlanKeys.lists()] as const,
  details: () => [...pricingPlanKeys.all, "detail"] as const,
  detail: (id: number) => [...pricingPlanKeys.details(), id] as const,
  history: () => [...pricingPlanKeys.all, "history"] as const,
  historyList: (query: GetPricingPlanHistoryQuery) =>
    [...pricingPlanKeys.history(), query] as const,
  paymentInfo: () => [...pricingPlanKeys.all, "payment-info"] as const,
};

export const featureKeys = {
  all: ["features"] as const,
  lists: () => [...featureKeys.all, "list"] as const,
  list: () => [...featureKeys.lists()] as const,
  details: () => [...featureKeys.all, "detail"] as const,
  detail: (key: string) => [...featureKeys.details(), key] as const,
};

// Pricing Plans Hooks
export const usePricingPlans = () => {
  return useQuery({
    queryKey: pricingPlanKeys.list(),
    queryFn: getPricingPlans,
  });
};

export const usePricingPlan = (id: number) => {
  return useQuery({
    queryKey: pricingPlanKeys.detail(id),
    queryFn: () => getPricingPlan(id),
    enabled: !!id,
  });
};

export const useCreatePricingPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePricingPlanRequest) => createPricingPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      message.success("Pricing plan created successfully");
    },
    onError: () => {
      message.error("Failed to create pricing plan");
    },
  });
};

export const useUpdatePricingPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePricingPlanRequest }) =>
      updatePricingPlan(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      message.success("Pricing plan updated successfully");
    },
    onError: () => {
      message.error("Failed to update pricing plan");
    },
  });
};

export const useUpdatePricingPlanPosition = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: UpdatePricingPlanPositionRequest;
    }) => updatePricingPlanPosition(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
    },
    onError: () => {
      message.error("Failed to update pricing plan position");
    },
  });
};

export const useUpdatePricingPlanFeatures = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: UpdatePricingPlanFeaturesRequest;
    }) => updatePricingPlanFeatures(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      message.success("Features updated successfully");
    },
    onError: () => {
      message.error("Failed to update features");
    },
  });
};

export const useDeletePricingPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deletePricingPlan(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      message.success("Pricing plan deleted successfully");
    },
    onError: () => {
      message.error("Failed to delete pricing plan");
    },
  });
};

// Features Hooks
export const useFeatures = () => {
  return useQuery({
    queryKey: featureKeys.list(),
    queryFn: getFeatures,
  });
};

export const useFeature = (key: string) => {
  return useQuery({
    queryKey: featureKeys.detail(key),
    queryFn: () => getFeature(key),
    enabled: !!key,
  });
};

export const useCreateFeature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateFeatureRequest) => createFeature(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      message.success("Feature created successfully");
    },
    onError: () => {
      message.error("Failed to create feature");
    },
  });
};

export const useUpdateFeature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, data }: { key: string; data: UpdateFeatureRequest }) =>
      updateFeature(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      message.success("Feature updated successfully");
    },
    onError: () => {
      message.error("Failed to update feature");
    },
  });
};

export const useUpdateFeatureOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      data,
    }: {
      key: string;
      data: UpdateFeatureOrderRequest;
    }) => updateFeatureOrder(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
    },
    onError: () => {
      message.error("Failed to update feature order");
    },
  });
};

export const useDeleteFeature = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => deleteFeature(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: featureKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      message.success("Feature deleted successfully");
    },
    onError: () => {
      message.error("Failed to delete feature");
    },
  });
};

// Subscription Hooks
export const useBuyPricingPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BuyPricingPlanRequest) => buyPricingPlan(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.history() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.paymentInfo() });
      const status = (res?.data as { status?: string } | undefined)?.status;
      message.success(
        status === "trial_started"
          ? "Free trial started"
          : "Subscription activated successfully",
      );
    },
    onError: (error: unknown) => {
      message.error(apiErrorMessage(error, "Failed to activate subscription"));
    },
  });
};

/**
 * Retry a failed subscription payment now. Messages are left to the caller so they can be
 * translated; success and failure both refresh payment-info (the banner reads it).
 */
export const useRetryFailedPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => retryFailedPayment(),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.paymentInfo() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.history() });
    },
  });
};

export const useAssignPricingPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AssignPricingPlanRequest) => assignPricingPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      message.success("Plan assigned successfully");
    },
    onError: (error) => {
      message.error(getApiErrorMessage(error, "Failed to assign plan"));
    },
  });
};

export const useCancelPricingPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CancelPricingPlanRequest) => cancelPricingPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.history() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.paymentInfo() });
      message.success("Subscription cancelled successfully");
    },
    onError: (error: unknown) => {
      message.error(apiErrorMessage(error, "Failed to cancel subscription"));
    },
  });
};

export const useResumePricingPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ResumePricingPlanRequest) => resumePricingPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.lists() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.history() });
      queryClient.invalidateQueries({ queryKey: pricingPlanKeys.paymentInfo() });
    },
    onError: (error: unknown) => {
      message.error(apiErrorMessage(error, "Failed to resume auto-renewal"));
    },
  });
};

// Get pricing plan history
export const usePricingPlanHistory = (
  query: GetPricingPlanHistoryQuery = {}
) => {
  return useQuery({
    queryKey: pricingPlanKeys.historyList(query),
    queryFn: () => getPricingPlanHistory(query),
    placeholderData: keepPreviousData,
  });
};

// Get payment info
export const usePaymentInfo = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: pricingPlanKeys.paymentInfo(),
    queryFn: getPaymentInfo,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Returns whether the current user has any active (purchased) pricing plan.
 * A plan is considered active when its `user_activated` flag is `true`.
 */
export const useHasActiveSubscription = () => {
  const { data: plans, isLoading, isFetching } = usePricingPlans();

  const hasActiveSubscription = Boolean(
    plans?.some((planData) => planData.plan.user_activated === true),
  );

  return { hasActiveSubscription, isLoading, isFetching };
};

